import { memo, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { createPortal } from "react-dom";
import { useStoreSelector } from "../../../../src/lib/store";
import { getLabActions } from "../labActions";
import { CHEAT_SHEET_HTML } from "../lib/helpCheatSheet";
import { patchUi, uiStore } from "../store/uiStore";
import "../helpCheatSheet.css";

const STORAGE_KEY = "audio-lab:help-modal";
const PANEL_WIDTH = 440;
const PANEL_MIN_HEIGHT = 280;

type PanelPos = { left: number; top: number };

function loadPos(): PanelPos | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<PanelPos>;
    if (typeof parsed.left === "number" && typeof parsed.top === "number") {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    // ignore
  }
  return undefined;
}

function savePos(pos: PanelPos): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

function defaultPos(): PanelPos {
  const left = Math.max(12, (window.innerWidth - PANEL_WIDTH) / 2);
  const top = Math.max(12, Math.min(96, window.innerHeight * 0.12));
  return { left, top };
}

function clampPos(pos: PanelPos, width: number, height: number): PanelPos {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(
    8,
    window.innerHeight - Math.min(height, window.innerHeight - 16) - 8,
  );
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}

/**
 * Floating, draggable tracker cheat-sheet (no backdrop).
 *
 * Position is React state (re-renders the shell on drag). Cheat-sheet HTML is a
 * memoized child so those moves do not re-apply dangerouslySetInnerHTML.
 */
export function HelpModal() {
  const open = useStoreSelector(uiStore, s => s.helpOpen);
  const panelRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<PanelPos>(() => loadPos() ?? { left: 12, top: 12 });
  const [dragging, setDragging] = useState(false);
  const dragCleanupRef = useRef<(() => void) | undefined>();

  useEffect(() => {
    if (!open) {
      return;
    }
    setPos(prev => {
      const el = panelRef.current;
      const width = el?.offsetWidth || PANEL_WIDTH;
      const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(loadPos() ?? defaultPos(), width, height);
      savePos(next);
      return next.left === prev.left && next.top === prev.top ? prev : next;
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onResize(): void {
      setPos(prev => {
        const el = panelRef.current;
        const width = el?.offsetWidth || PANEL_WIDTH;
        const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
        const next = clampPos(prev, width, height);
        savePos(next);
        return next;
      });
    }
    window.addEventListener("resize", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      dragCleanupRef.current?.();
      dragCleanupRef.current = undefined;
    };
  }, [open]);

  function close(): void {
    dragCleanupRef.current?.();
    dragCleanupRef.current = undefined;
    setDragging(false);
    patchUi({ helpOpen: false });
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (target?.closest("button")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }

    dragCleanupRef.current?.();

    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);

    const onMove = (moveEvent: PointerEvent): void => {
      const width = el.offsetWidth || PANEL_WIDTH;
      const height = el.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(
        {
          left: moveEvent.clientX - offsetX,
          top: moveEvent.clientY - offsetY,
        },
        width,
        height,
      );
      savePos(next);
      setPos(next);
    };

    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      ref={panelRef}
      className={`help-modal fixed z-50 flex max-h-[min(72vh,calc(100vh-24px))] w-[min(440px,calc(100vw-24px))] flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-[12px] text-lab-text shadow-[0_24px_48px_rgba(0,0,0,0.45)]${dragging ? " select-none" : ""}`}
      role="dialog"
      aria-modal="false"
      aria-label="Tracker help"
      style={{ left: pos.left, top: pos.top }}
    >
      <div
        className="help-modal-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-lab-line bg-lab-panel-bar px-[0.65rem] py-2"
        onPointerDown={beginDrag}
      >
        <span className="help-modal-title text-[12px] font-semibold text-lab-text">
          Tracker cheat sheet
        </span>
        <button
          type="button"
          className="help-modal-close rounded border border-transparent bg-transparent px-2 py-[0.2rem] text-[12px] text-lab-muted hover:border-lab-line hover:bg-white/[0.04] hover:text-lab-text"
          title="Close"
          aria-label="Close"
          onPointerDown={event => event.stopPropagation()}
          onClick={() => {
            close();
            getLabActions()?.setStatus?.("Tracker help closed.");
          }}
        >
          Close
        </button>
      </div>
      <HelpCheatSheetBody />
    </div>,
    document.body,
  );
}

/** Stable subtree — drag `setPos` must not re-inject cheat-sheet HTML. */
const HelpCheatSheetBody = memo(function HelpCheatSheetBody() {
  return (
    <div
      className="help-modal-body min-h-0 flex-1 overflow-auto px-[0.85rem] pt-3 pb-4 leading-[1.45]"
      dangerouslySetInnerHTML={{ __html: CHEAT_SHEET_HTML }}
    />
  );
});
